import React, { useState, useEffect } from 'react';
import WrestlerCard, { WrestlerCardStyles } from './WrestlerCard';
import { getWrestlerName } from './wrestlerUtils';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import DraftPage from './DraftPage';
import RosterReview from './RosterReview';
import HallOfFameSequence from './HallOfFameSequence';
import SeasonRecap from './SeasonRecap';

const placeholder = process.env.PUBLIC_URL + '/wrestlers/placeholder.png';
const getWrestlerRender = (w) => {
  if (!w) return null;
  if (w.customRender) return w.customRender;
  if (w.wrestler_id) return process.env.PUBLIC_URL + '/wrestlers/' + w.wrestler_id + '-render.png';
  return null;
};

const BRAND_LOGOS = {
  'Raw': process.env.PUBLIC_URL + '/brands/logos/RAW25_LOGO.png',
  'SmackDown': process.env.PUBLIC_URL + '/brands/logos/SMACKDOWN25_LOGO.png',
  'NXT': process.env.PUBLIC_URL + '/brands/logos/NXT_LOGO.png',
  'NXT 2.0': process.env.PUBLIC_URL + '/brands/logos/NXT20_LOGO.png',
  'WCW': process.env.PUBLIC_URL + '/brands/logos/WCW_LOGO.png',
  'ECW': process.env.PUBLIC_URL + '/brands/logos/ECW_LOGO.png',
  'NXT Mutiny': process.env.PUBLIC_URL + '/brands/logos/NXTMUTINY_LOGO.png',
};
const BRAND_BGS = {
  'Raw': process.env.PUBLIC_URL + '/brands/backgrounds/RAW25_BACKGROUND.png',
  'SmackDown': process.env.PUBLIC_URL + '/brands/backgrounds/SMACKDOWN25_BACKGROUND.png',
  'NXT': process.env.PUBLIC_URL + '/brands/backgrounds/NXT_BACKGROUND.png',
  'NXT 2.0': process.env.PUBLIC_URL + '/brands/backgrounds/NXT20_BACKGROUND.png',
  'WCW': process.env.PUBLIC_URL + '/brands/backgrounds/WCW_BACKGROUND.png',
  'ECW': process.env.PUBLIC_URL + '/brands/backgrounds/ECW_BACKGROUND.png',
  'NXT Mutiny': process.env.PUBLIC_URL + '/brands/backgrounds/NXTMUTINY_BACKGROUND.png',
};
const BRAND_COLORS = {
  'Raw': '#c0392b', 'SmackDown': '#1a6bb5', 'NXT': '#6c3483',
  'NXT 2.0': '#e91e8c', 'ECW': '#7b1fa2', 'WCW': '#e65100', 'NXT Mutiny': '#424242',
};

// Retentions par rang (inverse: 1er = 3, dernier = 6)
const getRetentionCount = (rank, totalBrands) => {
  var base = 3 + (rank - 1);
  return Math.min(base, 6);
};

const css = `
  * { box-sizing: border-box; }
  html { font-size: 133%; }
  .retain-card { transition: background 0.15s, border-color 0.15s, transform 0.15s; cursor: pointer; }
  .retain-card:hover { border-color: rgba(255,255,255,0.3) !important; transform: translateY(-2px); }
  .retain-card.selected { border-color: rgba(46,204,113,0.7) !important; background: rgba(46,204,113,0.08) !important; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
`;

const S = {
  anton: { fontFamily: "'Anton', sans-serif" },
  mono: { fontFamily: "'Monda', sans-serif" },
  panel: { background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px' },
  label: { fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)' },
};

// \u2500\u2500 Step 1: Season Summary & Ranking \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
const SeasonSummary = ({ gameData, myRank, retentionCount, totalBrands, onContinue, isChampionAndHof }) => {
  var brand = gameData?.brand || '';
  var brandColor = gameData?.brandColor || BRAND_COLORS[brand] || '#d4af37';
  var fans = gameData?.fans || 0;
  var season = gameData?.season || 1;
  var rivals = (gameData?.rivals || []).filter(Boolean);
  var victoryCriteria = gameData?.gameSettingsData?.victoryCriteria || 'fans';

  var myGm = gameData?.gm || '';
  var myBrandLogo = BRAND_LOGOS[brand] || gameData?.brandLogo || null;

  // HOF calculation — same logic as Dashboard
  var AUTO_FAN_THRESHOLDS = [2300000, 3400000, 4500000, 5250000, 6000000];
  var AUTO_PROFIT_THRESHOLDS = [2000000, 5250000, 8750000, 12500000, 16000000];
  var myHofActive = gameData?.hofActive || [];
  var myTotalProfits = (gameData?.seasonHistory || []).reduce((s, w) => s + (w.profits || 0), 0);
  var myAutoFan = AUTO_FAN_THRESHOLDS.filter(t => fans >= t).length;
  var myAutoProfit = AUTO_PROFIT_THRESHOLDS.filter(t => myTotalProfits >= t).length;
  var myHof = myHofActive.length + myAutoFan + myAutoProfit;

  var calcRivalHof = (r) => {
    var rHofActive = r.hofActive || [];
    var rFans = r.fans || 0;
    var rProfits = r.totalProfits || 0;
    var rAutoFan = AUTO_FAN_THRESHOLDS.filter(t => rFans >= t).length;
    var rAutoProfit = AUTO_PROFIT_THRESHOLDS.filter(t => rProfits >= t).length;
    return rHofActive.length + rAutoFan + rAutoProfit;
  };

  var ranking = [
    { name: brand, gm: myGm, logo: myBrandLogo, color: brandColor, fans: fans, hof: myHof, isMe: true, brandBg: BRAND_BGS[brand] || gameData?.brandBg || null },
    ...rivals.map(r => ({
      name: r.brand, gm: r.gm || '', logo: BRAND_LOGOS[r.brand] || r.brandLogo || null,
      color: BRAND_COLORS[r.brand] || r.brandColor || '#444',
      fans: r.fans || 0, hof: calcRivalHof(r), isMe: false,
      brandBg: BRAND_BGS[r.brand] || r.brandBg || null,
    }))
  ].sort((a, b) => {
    var valA = victoryCriteria === 'fans' ? a.fans : a.hof;
    var valB = victoryCriteria === 'fans' ? b.fans : b.hof;
    return valB - valA;
  });

  var isChampion = myRank === 1;
  var budget = gameData?.budget || 0;
  var formatFans = (v) => Math.round(Number(v)).toLocaleString('fr-FR').replace(/,/g, ' ');
  var formatBudget = (v) => '$' + Math.round(Number(v)).toLocaleString('fr-FR').replace(/,/g, ' ');
  var rankColors = ['#d4af37', '#95a5a6', '#e67e22', '#888'];

  var handleMainBtn = () => {
    onContinue();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.97)' }}>
      <style>{css}</style>
      <div style={{ width: '100%', maxWidth: '700px', display: 'flex', flexDirection: 'column', gap: '24px', padding: '40px 24px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ ...S.mono, fontSize: '0.55rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '3px', marginBottom: '8px' }}>END OF SEASON {season}</div>
          <div style={{ ...S.anton, fontSize: '3rem', letterSpacing: '4px', color: '#fff', lineHeight: 1 }}>FINAL STANDINGS</div>
        </div>

        {/* Ranking */}
        <div style={{ ...S.panel, overflow: 'hidden' }}>
          <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {ranking.map((entry, i) => {
              var entryBg = BRAND_BGS[entry.name] || entry.brandBg || null;
              var isFirst = i === 0;
              var bgOpacity = isFirst ? 0.35 : entry.isMe ? 0.28 : 0.14;
              return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '14px',
                padding: '12px 16px', borderRadius: '8px',
                border: '1px solid ' + (isFirst ? entry.color + '80' : entry.isMe ? entry.color + '55' : 'rgba(255,255,255,0.08)'),
                background: 'transparent',
                position: 'relative', overflow: 'hidden',
                boxShadow: isFirst
                  ? '0 0 24px ' + entry.color + '55, inset 0 0 30px ' + entry.color + '18'
                  : entry.isMe
                    ? '0 0 12px ' + entry.color + '33'
                    : 'none',
              }}>
                {entryBg && (
                  <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'url(' + entryBg + ')',
                    backgroundSize: 'cover', backgroundPosition: 'center right',
                    opacity: bgOpacity, zIndex: 0,
                  }} />
                )}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: isFirst
                    ? 'linear-gradient(90deg, ' + entry.color + '30 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.5) 100%)'
                    : entry.isMe
                      ? 'linear-gradient(90deg, ' + entry.color + '22 0%, rgba(0,0,0,0.4) 100%)'
                      : 'linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 100%)',
                  zIndex: 0,
                }} />
                <div style={{ ...S.anton, fontSize: '1.8rem', color: rankColors[i] || '#888', width: '32px', textAlign: 'center', flexShrink: 0, position: 'relative', zIndex: 1 }}>{i+1}</div>
                {entry.logo && <img loading="lazy" src={entry.logo} alt={entry.name} style={{ height: '28px', objectFit: 'contain', flexShrink: 0, position: 'relative', zIndex: 1 }} onError={e => { e.target.style.display='none'; }} />}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, position: 'relative', zIndex: 1 }}>
                  <div style={{ ...S.mono, fontSize: '0.55rem', color: isFirst ? '#fff' : entry.isMe ? '#fff' : 'rgba(255,255,255,0.6)', letterSpacing: '1px' }}>{entry.name}</div>
                  {entry.gm && <div style={{ ...S.mono, fontSize: '0.42rem', color: isFirst ? entry.color : entry.isMe ? entry.color : 'rgba(255,255,255,0.35)', letterSpacing: '0.5px' }}>{entry.gm}</div>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0, position: 'relative', zIndex: 1 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ ...S.mono, fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px', marginBottom: '2px' }}>FANS</div>
                    <div style={{ ...S.anton, fontSize: '1rem', color: (isFirst || entry.isMe) ? entry.color : 'rgba(255,255,255,0.4)' }}>{formatFans(entry.fans)}</div>
                  </div>
                  {entry.isMe && (
                    <>
                      <div style={{ width: '1px', height: '28px', background: 'rgba(255,255,255,0.08)' }} />
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ ...S.mono, fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px', marginBottom: '2px' }}>BUDGET</div>
                        <div style={{ ...S.anton, fontSize: '1rem', color: '#2ecc71' }}>{formatBudget(budget)}</div>
                      </div>
                    </>
                  )}
                  <div style={{ width: '1px', height: '28px', background: 'rgba(255,255,255,0.08)' }} />
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ ...S.mono, fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px', marginBottom: '2px' }}>HOF 🏆</div>
                    <div style={{ ...S.anton, fontSize: '1rem', color: (isFirst || entry.isMe) ? '#d4af37' : 'rgba(255,255,255,0.3)' }}>{entry.hof}</div>
                  </div>
                </div>
                {entry.isMe && <div style={{ ...S.mono, fontSize: '0.42rem', color: '#2ecc71', background: 'rgba(46,204,113,0.12)', border: '1px solid rgba(46,204,113,0.3)', borderRadius: '3px', padding: '2px 8px', flexShrink: 0, position: 'relative', zIndex: 1 }}>YOU</div>}
              </div>
              );
            })}
          </div>
        </div>

        {/* Retention info */}
        <div style={{ ...S.panel, padding: '16px 20px', border: '1px solid ' + (isChampion ? 'rgba(212,175,55,0.4)' : 'rgba(255,255,255,0.1)'), background: isChampion ? 'rgba(212,175,55,0.06)' : 'rgba(255,255,255,0.02)', textAlign: 'center' }}>
          <div style={{ ...S.label, marginBottom: '8px' }}>YOUR RANK — {myRank}{myRank === 1 ? 'ST' : myRank === 2 ? 'ND' : myRank === 3 ? 'RD' : 'TH'} PLACE</div>
          <div style={{ ...S.anton, fontSize: '1.2rem', color: isChampion ? '#d4af37' : '#fff' }}>
            {retentionCount} SUPERSTAR{retentionCount > 1 ? 'S' : ''} TO KEEP
          </div>
          <div style={{ ...S.mono, fontSize: '0.44rem', color: 'rgba(255,255,255,0.35)', marginTop: '6px' }}>
            {isChampion
              ? 'Your Hall of Fame ceremony awaits, then review the season — build your season ' + (season + 1) + ' roster'
              : 'Select the superstars you want to keep for season ' + (season + 1)}
          </div>
        </div>

        <button onClick={handleMainBtn}
          style={{ ...S.anton, fontSize: '0.85rem', letterSpacing: '3px', background: isChampion ? 'rgba(212,175,55,0.15)' : 'rgba(212,175,55,0.12)', border: '1px solid ' + (isChampion ? 'rgba(212,175,55,0.8)' : 'rgba(212,175,55,0.6)'), color: '#d4af37', padding: '14px', borderRadius: '8px', cursor: 'pointer' }}>
          {isChampionAndHof ? '🏆 HALL OF FAME CEREMONY →' : 'SEASON RECAP →'}
        </button>
      </div>
    </div>
  );
};

// \u2500\u2500 Step 2: Superstar Retention \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
const RetentionPicker = ({ gameData, retentionCount, onConfirm }) => {
  var brand = gameData?.brand || '';
  var brandColor = gameData?.brandColor || BRAND_COLORS[brand] || '#d4af37';
  var roster = gameData?.roster || [];
  var champions = gameData?.champions || {};
  var champMap = champions?.champions || {};
  var [selected, setSelected] = useState([]);
  var [search, setSearch] = useState('');
  var [filterGender, setFilterGender] = useState('ALL');

  var champIds = Object.values(champMap).flat().map(w => w && w.wrestler_id).filter(Boolean);

  var filtered = roster
    .filter(w => filterGender === 'ALL' || w.gender === (filterGender === 'MALE' ? 'M' : 'F'))
    .filter(w => !search || w.name.toLowerCase().includes(search.toLowerCase()));

  var toggle = (w) => {
    var exists = selected.some(s => s.wrestler_id === w.wrestler_id);
    if (exists) { setSelected(selected.filter(s => s.wrestler_id !== w.wrestler_id)); }
    else if (selected.length < retentionCount) { setSelected([...selected, w]); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', flexDirection: 'column', background: 'rgba(0,0,0,0.97)' }}>
      <style>{css + WrestlerCardStyles}</style>

      {/* Header */}
      <div style={{ padding: '20px 28px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <div style={{ ...S.label, marginBottom: '4px' }}>SEASON RETENTION</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ ...S.anton, fontSize: '1.2rem', letterSpacing: '2px', color: '#fff' }}>SELECT YOUR SUPERSTARS</div>
          <div style={{ ...S.anton, fontSize: '1rem', color: selected.length >= retentionCount ? '#2ecc71' : brandColor }}>
            {selected.length} / {retentionCount}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ padding: '12px 28px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: '10px', flexShrink: 0 }}>
        <input style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '6px 12px', color: '#fff', fontSize: '0.55rem', fontFamily: "'Monda',sans-serif", outline: 'none', width: '200px' }}
          placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
        {['ALL','MALE','FEMALE'].map(f => (
          <button key={f} onClick={() => setFilterGender(f)}
            style={{ ...S.mono, fontSize: '0.44rem', background: filterGender === f ? 'rgba(255,255,255,0.1)' : 'transparent', border: '1px solid ' + (filterGender === f ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.12)'), color: filterGender === f ? '#fff' : 'rgba(255,255,255,0.4)', padding: '5px 12px', borderRadius: '3px', cursor: 'pointer' }}>
            {f}
          </button>
        ))}
        {/* Selected preview */}
        {selected.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginLeft: 'auto' }}>
            {selected.map((w, i) => (
              <img loading="lazy" key={i} src={getWrestlerRender(w) || placeholder} alt={w.name}
                style={{ width: '28px', height: '36px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.8))' }} />
            ))}
          </div>
        )}
      </div>

      {/* Grid */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 28px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
          {filtered.map((w, i) => {
            var isSel = selected.some(s => s.wrestler_id === w.wrestler_id);
            var isDisabled = !isSel && selected.length >= retentionCount;
            return (
              <div key={i} onClick={() => !isDisabled && toggle(w)}
                style={{ position: 'relative', opacity: isDisabled ? 0.35 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer', transition: 'opacity 0.15s' }}>
                {/* Selection overlay */}
                {isSel && (
                  <div style={{ position: 'absolute', inset: 0, zIndex: 20, borderRadius: '12px', border: '2px solid #2ecc71', boxShadow: '0 0 16px rgba(46,204,113,0.4)', pointerEvents: 'none' }} />
                )}
                <WrestlerCard
                  wrestler={w}
                  brandColor={isSel ? '#2ecc71' : brandColor}
                  champions={champions}
                  roster={roster}
                  rivalries={[]}
                  showActionButtons={false}
                  variant="roster"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '14px 28px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <div style={{ ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.4)' }}>
          {retentionCount - selected.length > 0
            ? `${retentionCount - selected.length} slot${retentionCount - selected.length > 1 ? 's' : ''} remaining`
            : '\u2713 Selection complete'}
        </div>
        <button onClick={() => onConfirm(selected)}
          style={{ ...S.anton, fontSize: '0.75rem', letterSpacing: '2px', background: 'rgba(46,204,113,0.12)', border: '1px solid rgba(46,204,113,0.6)', color: '#2ecc71', padding: '10px 28px', borderRadius: '6px', cursor: 'pointer' }}>
          CONFIRM →
        </button>
      </div>
    </div>
  );
};

// \u2500\u2500 Main EndOfSeasonFlow \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
export default function EndOfSeasonFlow({ gameData, onUpdateGame, onComplete }) {
  var [step, setStep] = useState('summary'); // summary | recap | retention | draft | review
  var [retainedWrestlers, setRetainedWrestlers] = useState([]);
  var [officialWrestlers, setOfficialWrestlers] = useState([]);
  var [newRoster, setNewRoster] = useState([]);

  var brand = gameData?.brand || '';
  var brandColor = gameData?.brandColor || BRAND_COLORS[brand] || '#d4af37';
  var fans = gameData?.fans || 0;
  var rivals = (gameData?.rivals || []).filter(Boolean);
  var victoryCriteria = gameData?.gameSettingsData?.victoryCriteria || 'fans';

  // Compute ranking
  var ranking = [
    { name: brand, value: victoryCriteria === 'fans' ? fans : 0, isMe: true },
    ...rivals.map(r => ({ name: r.brand, value: r.fans || 0, isMe: false }))
  ].sort((a, b) => b.value - a.value);
  var myRank = ranking.findIndex(e => e.isMe) + 1;
  var totalBrands = ranking.length;
  var retentionCount = getRetentionCount(myRank, totalBrands);

  // HOF goal check
  var AUTO_FAN_THRESHOLDS_EOF = [2300000, 3400000, 4500000, 5250000, 6000000];
  var AUTO_PROFIT_THRESHOLDS_EOF = [2000000, 5250000, 8750000, 12500000, 16000000];
  var hofGoal = gameData?.hofTrophies ?? gameData?.gameSettingsData?.hofTrophies ?? 10;
  var hofActive = gameData?.hofActive || [];
  var totalProfitsEOF = (gameData?.seasonHistory || []).reduce((s, w) => s + (w.profits || 0), 0);
  var autoFanEOF = AUTO_FAN_THRESHOLDS_EOF.filter(t => fans >= t).length;
  var autoProfitEOF = AUTO_PROFIT_THRESHOLDS_EOF.filter(t => totalProfitsEOF >= t).length;
  var totalTrophies = hofActive.length + autoFanEOF + autoProfitEOF;
  var hofGoalReached = totalTrophies >= hofGoal;
  var isChampionAndHof = myRank === 1 && hofGoalReached;

  // Load DB for draft
  useEffect(() => {
    var uid = auth.currentUser && auth.currentUser.uid;
    if (!uid) return;
    var version = gameData?.gameVersion;
    var baseCollectionNames = version === '2k25' ? ['wrestlers_2k25']
      : version === '2k26' ? ['wrestlers_2k26']
      : ['wrestlers_2k25', 'wrestlers_2k26']; // legacy game, version unknown — merge both
    var promises = [
      getDocs(query(collection(db, 'officialWrestlers'), where('userId', '==', uid))),
      ...baseCollectionNames.map(function(name) { return getDocs(collection(db, name)); }),
    ];
    Promise.all(promises).then(function(snaps) {
      var custom = snaps[0].docs.map(d => d.data());
      var base = [];
      for (var i = 1; i < snaps.length; i++) base.push(...snaps[i].docs.map(d => d.data()));
      var merged = [];
      var seen = new Set();
      [...base, ...custom].forEach(function(w) {
        var id = String(w.wrestler_id);
        if (!seen.has(id)) { merged.push(w); seen.add(id); }
      });
      setOfficialWrestlers(merged);
    });
  }, [gameData?.gameVersion]);

  // Compute new champions after retention
  var computeNewChampions = (retained) => {
    var champions = gameData?.champions || {};
    var champMap = { ...(champions.champions || {}) };
    var newChampMap = {};

    Object.entries(champMap).forEach(([key, champs]) => {
      if (!champs || champs.length === 0) return;
      var retainedIds = retained.map(w => w.wrestler_id);
      // All champs must be retained
      var allRetained = champs.every(c => c && retainedIds.includes(c.wrestler_id));
      if (allRetained) newChampMap[key] = champs;
      // else: vacant (not added)
    });

    return { ...champions, champions: newChampMap };
  };

  var handleRetentionConfirm = (retained) => {
    setRetainedWrestlers(retained);
    setStep('draft');
  };

  var handleDraftComplete = (draftedPicks) => {
    // Merge retained + new draft picks (avoid duplicates)
    var retainedIds = retainedWrestlers.map(w => w.wrestler_id);
    var newPicks = draftedPicks.filter(w => !retainedIds.includes(w.wrestler_id));
    var fullRoster = [...retainedWrestlers.map(w => ({ ...w, draftPick: true })), ...newPicks.map(w => ({ ...w, draftPick: true }))];
    setNewRoster(fullRoster);
    setStep('review');
  };

  var handleReviewConfirm = () => {
    var newChampions = computeNewChampions(retainedWrestlers);
    var updatedGame = {
      ...gameData,
      season: (gameData?.season || 1) + 1,
      week: 1,
      roster: newRoster,
      champions: newChampions,
      rivalries: [],
      pastRivalries: [],
      bookingSlots: [],
      bookingFinished: false,
      pleBooking: {},
      pleMidCardSlots: [],
      lastWeek: [],
      _showWeekStart: true,
    };
    if (onUpdateGame) onUpdateGame(updatedGame);
    if (onComplete) onComplete(updatedGame);
  };

  if (step === 'summary') return (
    <SeasonSummary
      gameData={gameData}
      myRank={myRank}
      retentionCount={retentionCount}
      totalBrands={totalBrands}
      isChampionAndHof={isChampionAndHof}
      onContinue={() => {
        // Si 1er ET HOF goal atteint → HOF ceremony avant recap
        // Sinon → recap directement
        if (isChampionAndHof) {
          setStep('hof');
        } else {
          setStep('recap');
        }
      }}
    />
  );

  if (step === 'hof') return (
    <HallOfFameSequence
      gameData={gameData}
      onClose={() => setStep('recap')}
    />
  );

  if (step === 'recap') return (
    <SeasonRecap
      gameData={{ ...gameData, _myRank: myRank }}
      onContinue={() => setStep('retention')}
    />
  );

  if (step === 'retention') return (
    <RetentionPicker
      gameData={gameData}
      retentionCount={retentionCount}
      onConfirm={handleRetentionConfirm}
    />
  );

  if (step === 'draft') return (
    <DraftPage
      roster={officialWrestlers}
      brand={brand}
      brandColor={brandColor}
      preSelected={retainedWrestlers}
      onComplete={handleDraftComplete}
      onBack={() => setStep('retention')}
    />
  );

  if (step === 'review') return (
    <RosterReview
      roster={newRoster}
      brand={brand}
      onBack={() => setStep('draft')}
      onConfirm={handleReviewConfirm}
    />
  );

  return null;
}
